import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SuperAdminAuthProvider, useSuperAdminAuth } from './hooks/useSuperAdminAuth';
import { PwaProvider } from './contexts/PwaContext';
import { LoadingProvider } from './contexts/LoadingContext';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import DownloadPage from './pages/DownloadPage';
import Dashboard from './pages/Dashboard';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import { ROUTES } from './constants';
import GearLoader from './components/ui/GearLoader';
import GlobalTruckLoader from './components/ui/GlobalTruckLoader';
import { GlobalToastContainer } from './components/ui/Toast';
import { GlobalErrorDialogContainer } from './components/ui/GlobalErrorDialog';
import UpdatePrompt from './components/ui/UpdatePrompt';
import ScrollToTop from './components/utils/ScrollToTop';
import './styles/App.css';

// Lazy load pages for code splitting
const POListPage = lazy(() => import('./pages/PurchaseOrders/POListPage'));
const POCreatePage = lazy(() => import('./pages/PurchaseOrders/POCreatePage'));
const POEditPage = lazy(() => import('./pages/PurchaseOrders/POEditPage'));
const PODetailPage = lazy(() => import('./pages/PurchaseOrders/PODetailPage'));
const InventoryListPage = lazy(() => import('./pages/Inventory/InventoryListPage'));
const InventoryCreatePage = lazy(() => import('./pages/Inventory/InventoryCreatePage'));
const InventoryDetailPage = lazy(() => import('./pages/Inventory/InventoryDetailPage'));
const SupplierListPage = lazy(() => import('./pages/Suppliers/SupplierListPage'));
const SupplierCreatePage = lazy(() => import('./pages/Suppliers/SupplierCreatePage'));
const SupplierDetailPage = lazy(() => import('./pages/Suppliers/SupplierDetailPage'));
const SupplierDocumentsPage = lazy(() => import('./pages/Suppliers/SupplierDocumentsPage'));
const MachinesListPage = lazy(() => import('./pages/Machines/MachinesListPage'));
const MachineCreatePage = lazy(() => import('./pages/Machines/MachineCreatePage'));
const MachineDetailPage = lazy(() => import('./pages/Machines/MachineDetailPage'));
const ReportsPage = lazy(() => import('./pages/Reports/ReportsPage'));
const MachineExpenseDetailPage = lazy(() => import('./pages/Reports/MachineExpenseDetailPage'));
// NOTE: FeatureConfigPage removed - feature_list table has been deleted
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const UserListPage = lazy(() => import('./pages/Users/UserListPage'));
const UserCreatePage = lazy(() => import('./pages/Users/UserCreatePage'));
const UserEditPage = lazy(() => import('./pages/Users/UserEditPage'));
const UserDetailPage = lazy(() => import('./pages/Users/UserDetailPage'));
const DocumentListPage = lazy(() => import('./pages/Documents/DocumentListPage'));
const BankDetailsListPage = lazy(() => import('./pages/BankDetails/BankDetailsListPage'));
const TermsListPage = lazy(() => import('./pages/Terms/TermsListPage'));
const TenantListPage = lazy(() => import('./pages/Tenants/TenantListPage'));
const TenantCreatePage = lazy(() => import('./pages/Tenants/TenantCreatePage'));
const TenantEditPage = lazy(() => import('./pages/Tenants/TenantEditPage'));
const TenantDocumentsPage = lazy(() => import('./pages/Tenants/TenantDocumentsPage'));
const TenantBankDetailsPage = lazy(() => import('./pages/Tenants/TenantBankDetailsPage'));
const MyTenantPage = lazy(() => import('./pages/Tenants/MyTenantPage'));
// NOTE: TenantDefaultConfigPage removed - feature_defaults table has been deleted
const FeaturePermissionConfigPage = lazy(() => import('./pages/Configuration/FeaturePermissionConfigPage'));
const SuperAdminUserListPage = lazy(() => import('./pages/SuperAdmin/SuperAdminUserListPage'));
const SuperAdminUserCreatePage = lazy(() => import('./pages/SuperAdmin/SuperAdminUserCreatePage'));
const SuperAdminUserEditPage = lazy(() => import('./pages/SuperAdmin/SuperAdminUserEditPage'));
const SuperAdminUserViewPage = lazy(() => import('./pages/SuperAdmin/SuperAdminUserViewPage'));
const SuperAdminProfilePage = lazy(() => import('./pages/SuperAdmin/SuperAdminProfilePage'));
const SupplierResponsePage = lazy(() => import('./pages/PurchaseOrders/SupplierResponsePage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <GearLoader text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <GearLoader text="Loading..." />
      </div>
    );
  }

  return isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : children;
};

// Protected Super Admin Route Component
const ProtectedSuperAdminRoute = ({ children }) => {
  const { isSuperAdminAuthenticated, isLoading } = useSuperAdminAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <GlobalTruckLoader />
      </div>
    );
  }

  if (!isSuperAdminAuthenticated) {
    return <Navigate to={ROUTES.SUPER_ADMIN_LOGIN} replace />;
  }

  return children;
};

// Public Super Admin Route Component (redirects to dashboard if already authenticated)
const PublicSuperAdminRoute = ({ children }) => {
  const { isSuperAdminAuthenticated, isLoading } = useSuperAdminAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <GlobalTruckLoader />
      </div>
    );
  }

  return isSuperAdminAuthenticated ? <Navigate to={ROUTES.SUPER_ADMIN_DASHBOARD} replace /> : children;
};

// Loading fallback component
const PageLoader = () => (
  <div className="loading-container">
    <GearLoader text="Loading page..." />
  </div>
);

function App() {
  return (
    <PwaProvider>
      <AuthProvider>
        <SuperAdminAuthProvider>
          <LoadingProvider>
            <Router>
              <div className="App">
                <ScrollToTop />
                <GlobalTruckLoader />
                <UpdatePrompt />
                <Routes>
                  {/* Public Landing Page - redirects to dashboard if authenticated */}
                  <Route path={ROUTES.LANDING} element={<PublicRoute><LandingPage /></PublicRoute>} />
                  <Route path={ROUTES.DOWNLOAD} element={<DownloadPage />} />

                  {/* Supplier Response - Public (no auth required) */}
                  <Route
                    path={ROUTES.SUPPLIER_RESPONSE}
                    element={
                      <Suspense fallback={<div className="loading-container"><GearLoader text="Loading..." /></div>}>
                        <SupplierResponsePage />
                      </Suspense>
                    }
                  />

                  {/* Public Routes */}
                  <Route
                    path={ROUTES.LOGIN}
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    }
                  />
                  <Route
                    path={ROUTES.FORGOT_PASSWORD}
                    element={
                      <PublicRoute>
                        <Suspense fallback={<PageLoader />}>
                          <ForgotPassword />
                        </Suspense>
                      </PublicRoute>
                    }
                  />
                  <Route
                    path={ROUTES.RESET_PASSWORD}
                    element={
                      <PublicRoute>
                        <Suspense fallback={<PageLoader />}>
                          <ResetPassword />
                        </Suspense>
                      </PublicRoute>
                    }
                  />

                  {/* Super Admin Public Routes */}
                  <Route
                    path={ROUTES.SUPER_ADMIN_LOGIN}
                    element={
                      <PublicSuperAdminRoute>
                        <SuperAdminLogin />
                      </PublicSuperAdminRoute>
                    }
                  />

                  {/* Super Admin Protected Routes */}
                  <Route
                    path={ROUTES.SUPER_ADMIN_DASHBOARD}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminDashboard />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_PROFILE}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminProfilePage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />

                  {/* Super Admin Tenant Routes */}
                  <Route
                    path={ROUTES.SUPER_ADMIN_TENANTS}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TenantListPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_TENANT_CREATE}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TenantCreatePage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_TENANT_EDIT}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TenantEditPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_TENANT_DOCUMENTS}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TenantDocumentsPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_TENANT_BANK_DETAILS}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TenantBankDetailsPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  {/* NOTE: TenantDefaultConfigPage route removed - feature_defaults table has been deleted */}

                  {/* Super Admin User Routes */}
                  <Route
                    path={ROUTES.SUPER_ADMIN_USERS}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminUserListPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_USER_CREATE}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminUserCreatePage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_USER_VIEW}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminUserViewPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPER_ADMIN_USER_EDIT}
                    element={
                      <ProtectedSuperAdminRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SuperAdminUserEditPage />
                        </Suspense>
                      </ProtectedSuperAdminRoute>
                    }
                  />

                  {/* Protected Routes */}
                  <Route
                    path={ROUTES.DASHBOARD}
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Purchase Orders Routes */}
                  <Route
                    path={ROUTES.PURCHASE_ORDERS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <POListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.PO_CREATE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <POCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.PO_EDIT}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <POEditPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.PO_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <PODetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Inventory Routes */}
                  <Route
                    path={ROUTES.INVENTORY}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <InventoryListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.INVENTORY_CREATE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <InventoryCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.INVENTORY_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <InventoryDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Supplier Routes */}
                  <Route
                    path={ROUTES.SUPPLIERS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SupplierListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPPLIER_CREATE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SupplierCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPPLIER_DOCUMENTS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SupplierDocumentsPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SUPPLIER_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <SupplierDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Machines Routes */}
                  <Route
                    path={ROUTES.MACHINES}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MachinesListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.MACHINE_CREATE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MachineCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.MACHINE_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MachineDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* NOTE: Configuration Routes removed - feature_list table has been deleted */}

                  {/* User Management */}
                  <Route
                    path={ROUTES.USERS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <UserListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <UserDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_EDIT}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <UserEditPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_CREATE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <UserCreatePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Profile Routes */}
                  <Route
                    path={ROUTES.PROFILE}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <ProfilePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Reports Routes */}
                  <Route
                    path={ROUTES.REPORTS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <ReportsPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.REPORT_MACHINE_DETAIL}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MachineExpenseDetailPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Documents Routes */}
                  <Route
                    path={ROUTES.DOCUMENTS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <DocumentListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Bank Details Routes */}
                  <Route
                    path={ROUTES.BANK_DETAILS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <BankDetailsListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Terms & Conditions Routes */}
                  <Route
                    path={ROUTES.TERMS}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <TermsListPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* My Tenant Route */}
                  <Route
                    path={ROUTES.MY_TENANT}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <MyTenantPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Configuration Route - SYSTEM_ADMIN only */}
                  <Route
                    path={ROUTES.CONFIGURATION}
                    element={
                      <ProtectedRoute>
                        <Suspense fallback={<PageLoader />}>
                          <FeaturePermissionConfigPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all route - redirect to landing */}
                  <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
                </Routes>

                {/* Global Toast Notifications */}
                <GlobalToastContainer />
                {/* Global Error Dialog */}
                <GlobalErrorDialogContainer />
              </div>
            </Router>
          </LoadingProvider>
        </SuperAdminAuthProvider>
      </AuthProvider>
    </PwaProvider>
  );
}

export default App;